import React, { useState, useRef } from 'react';
import { Box, TextField } from '@mui/material';
import { TP_COLORS } from '../../../constants';

const CODE_LENGTH = 6;

type CodeInputProps = {
  onChange?: (code: string) => void;
};

const CodeInput: React.FC<CodeInputProps> = ({ onChange }) => {
  const [values, setValues] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return; // Allow only single digit

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    if (onChange) {
      onChange(newValues.join(''));
    }

    if (value && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (key: string, index: number) => {
    if (key === 'Backspace' && !values[index] && index > 0) {
      const prev = inputsRef.current[index - 1];
      prev?.focus();

      if (onChange) {
        onChange(values.join(''));
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only process if the pasted data contains only digits
    if (!/^\d+$/.test(pastedData)) return;

    // Take only the first 6 digits if more are pasted
    const digits = pastedData.slice(0, CODE_LENGTH).split('');

    // Fill the values array with the pasted digits
    const newValues = [...values];
    digits.forEach((digit, index) => {
      if (index < CODE_LENGTH) {
        newValues[index] = digit;
      }
    });

    setValues(newValues);

    if (onChange) {
      onChange(newValues.join(''));
    }

    // Focus the next empty input or the last input
    const nextEmptyIndex = newValues.findIndex((val) => !val);
    if (nextEmptyIndex !== -1) {
      inputsRef.current[nextEmptyIndex]?.focus();
    } else {
      inputsRef.current[CODE_LENGTH - 1]?.focus();
    }
  };

  return (
    <Box display="flex" gap={1} justifyContent="center">
      {values.map((val, index) => (
        <TextField
          key={index}
          inputRef={(el) => {
            inputsRef.current[index] = el;
          }}
          value={val}
          autoComplete="off"
          onChange={(e) => handleChange(e.target.value, index)}
          onKeyDown={(e) => handleKeyDown(e.key, index)}
          onPaste={handlePaste}
          inputProps={{
            maxLength: 1,
            style: {
              padding: 0,
              textAlign: 'center',
              fontSize: '24px',
              width: '52px',
              height: '69px',
              borderRadius: '10px',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: val ? TP_COLORS.green.light : '#ccc',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: val ? TP_COLORS.green.light : undefined,
              },
              '&.Mui-focused fieldset': {
                borderColor: val ? TP_COLORS.green.light : undefined,
                borderWidth: '1px',
              },
            },
            '& .MuiInputBase-input': {
              color: val ? TP_COLORS.green.light : '#000',
            },
          }}
          variant="outlined"
        />
      ))}
    </Box>
  );
};

export default CodeInput;
